import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { api, errorText } from '../api';
import { LegalLinks } from '../components/LegalLinks';
import { LangSwitch, t } from '../i18n';
import { useApi } from '../lib/useApi';
import { markenName } from '../produkt';
import { homeOf } from './redirect';
import { useSession } from './session';

interface TokenInfo {
  purpose: 'invite' | 'reset';
  email: string;
  name: string;
}

// Passwort festlegen über einen Einmal-Link (Einladung der Kanzlei oder Passwort zurücksetzen).
// Danach ist die Person angemeldet und landet in ihrer App (Kanzlei oder Mandanten-App).
export function PasswordPage() {
  const { token = '' } = useParams();
  const { refresh } = useSession();
  const navigate = useNavigate();
  const info = useApi<TokenInfo>(`/api/auth/password/${encodeURIComponent(token)}`);
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const invite = info.data?.purpose === 'invite';

  useEffect(() => {
    document.title = `${invite ? t('Passwort festlegen') : t('Neues Passwort')} · ${markenName()}`;
  }, [invite]);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    if (password.length < 8) {
      setError(t('Das Passwort muss mindestens 8 Zeichen haben.'));
      document.getElementById('pw-new')?.focus();
      return;
    }
    setBusy(true);
    try {
      await api.post('/api/auth/password/set', { token, password });
      const me = await refresh();
      navigate(me ? homeOf(me) : '/app', { replace: true });
    } catch (err) {
      setError(errorText(err));
      setBusy(false);
    }
  }

  return (
    <div className="auth">
      <div className="auth-box">
        <div className="auth-brand">
          <span className="logo" role="img" aria-label={markenName()} />
          <LangSwitch />
        </div>
        {info.loading ? (
          <div className="spinner" aria-label={t('Lädt')} />
        ) : info.error || !info.data ? (
          <>
            <h1>{t('Link ungültig')}</h1>
            <p className="notice bad" role="alert">
              {info.error ?? t('Dieser Link ist ungültig.')}
            </p>
            <p className="auth-foot">
              <Link to="/passwort-vergessen">{t('Neuen Link anfordern')}</Link> · <Link to="/login">{t('Zur Anmeldung')}</Link>
            </p>
          </>
        ) : (
          <>
            <h1>{invite ? t('Passwort festlegen') : t('Neues Passwort')}</h1>
            <p>
              {invite
                ? t('Hallo {name}, legen Sie ein Passwort für Ihren Zugang fest.', { name: info.data.name })
                : t('Hallo {name}, legen Sie ein neues Passwort fest.', { name: info.data.name })}
            </p>
            <form className="card" onSubmit={submit} noValidate>
              <label className="field">
                <span>{t('E-Mail')}</span>
                <input type="email" value={info.data.email} readOnly autoComplete="username" />
              </label>
              <label className="field">
                <span>{invite ? t('Passwort (mindestens 8 Zeichen)') : t('Neues Passwort (mindestens 8 Zeichen)')}</span>
                <input
                  id="pw-new"
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  maxLength={200}
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  aria-invalid={error ? true : undefined}
                  aria-describedby={error ? 'pw-err' : undefined}
                />
              </label>
              {error && (
                <p className="notice bad" role="alert" id="pw-err">
                  {error}
                </p>
              )}
              <button className="btn primary block" disabled={busy}>
                {busy ? t('Einen Moment …') : t('Passwort speichern')}
              </button>
            </form>
          </>
        )}
        <LegalLinks mitAgb />
      </div>
    </div>
  );
}
